import { SIDES, type Side } from './gait'
import { contactPoints, type Pose, type Skeleton } from './kinematics'
import type { Vec3 } from './vec'

/**
 * Das Zehengrundgelenk — eine Feder statt eines Antriebs.
 *
 * DER BEFUND, DER DIE ENTSCHEIDUNG TRÄGT: das Gelenk nimmt über den Zyklus nur
 * Energie AUF. In jeder Gangart ist seine Nettoarbeit negativ. Ein Gelenk, das
 * ausschliesslich aufnimmt, braucht keinen Antrieb — es braucht etwas, das
 * speichert.
 *
 * DAZU DIE TRÄGHEITSREGEL, DIE SCHON P00 AM KNIE BEGRÜNDET. Ein Antrieb am
 * Vorfuss sitzt rund einen Meter von der Hüfte entfernt; 0,8 kg bringen dort
 * etwa 0,8 kg·m² in die Hüftrechnung ein — mehr als der gesamte Gewinn, den die
 * proximale Kniekonstruktion einbringt. Man bezahlte zweimal: den Motor und die
 * Trägheit, die er der Hüfte aufbürdet.
 *
 * WAS DAFÜR EINGEBAUT WIRD: eine progressive Feder mit Vorspannung und
 * Anschlag. Die Vorspannung hält die Zehe in der Schwungphase neutral, damit
 * sie nicht schleift; der lineare Bereich trägt den Abdruck; kurz vor dem
 * Anschlag wird die Kennlinie progressiv, damit der Stopp kein Schlag ist.
 *
 * IM ACHSBAUM IST DAS GELENK DAMIT PASSIV. Es hat eine Winkelfreiheit und
 * zählt in der Achszahl; es hat keinen Motor und zählt nicht in der Motorzahl.
 * Genau darin besteht der Unterschied zwischen aktiv und passiv, und er steht
 * nicht in einem Kommentar, sondern in `mechanism.ts`.
 *
 * WAS DIE FEDER NICHT KANN: keine gangabhängige Steifigkeit — Gehen möchte
 * weicher, der Sprint steifer, eine Feder ist der Kompromiss. Und keinen
 * aktiven Zehenabdruck am Ende der Standphase; nach der Messung ist der klein.
 * Dafür gibt es etwas geschenkt: ein Winkelgeber an der Zehe liefert über die
 * bekannte Kennlinie unmittelbar die Zehenlast — ein Kontaktsignal ohne eigenen
 * Kraftsensor.
 */

/** Die Auslegung der Feder. */
export interface ToeSpring {
  /** Vorspannung in Nm — hält die Zehe in der Luft in Neutrallage. */
  preload: number
  /** Federrate im linearen Bereich, Nm je Grad. */
  rate: number
  /** Anschlagwinkel in Grad. */
  stop: number
  /** Progressionsbeiwert oberhalb von 70 % des Anschlags, Nm je Grad². */
  progression: number
  /** Anteil der gespeicherten Energie, der beim Abheben zurückkommt. */
  recovery: number
}

export const TOE_SPRING: ToeSpring = {
  preload: 5,
  rate: 3.4,
  stop: 42,
  progression: 0.5,
  recovery: 0.75,
}

/** Ab welchem Anteil des Anschlagwinkels die Kennlinie progressiv wird. */
const PROGRESSIVE_FROM = 0.7

/**
 * Die Kennlinie: welches Moment die Feder bei diesem Winkel hält, Nm.
 *
 * Unter null gibt es nichts zu halten — die Zehe hebt vom Anschlag ab, und die
 * Feder drückt nicht in die andere Richtung.
 */
export function toeMoment(angle: number, spring: ToeSpring = TOE_SPRING): number {
  if (angle <= 0) return 0
  const knee = PROGRESSIVE_FROM * spring.stop
  const extra = angle > knee ? spring.progression * (angle - knee) ** 2 : 0
  return spring.preload + spring.rate * angle + extra
}

/**
 * Der Winkel, bei dem die Feder dieses Moment hält — die Umkehrung der Kennlinie.
 *
 * WARUM UMGEKEHRT UND NICHT ABGELESEN: der Zehenwinkel im Gangbild ist
 * VORGEGEBEN und federt nicht mit. Aus dem Kanal käme deshalb der Winkel, den
 * die Aufzeichnung vorschreibt, und nicht der, den die Feder unter dieser Last
 * einnähme. Ein mitfederndes Gelenk verschöbe die Druckmittelpunktbahn leicht;
 * für die Auslegung der Feder genügt dieser Weg, und dass er einer ist, steht
 * in der Oberfläche.
 *
 * Über Intervallhalbierung und nicht analytisch: die Kennlinie ist stückweise
 * definiert, und eine geschlossene Umkehrung wäre zwei Fälle, von denen einer
 * beim nächsten Beiwert falsch wird.
 */
export function toeDeflection(moment: number, spring: ToeSpring = TOE_SPRING): number {
  if (moment <= spring.preload) return 0
  let lo = 0
  let hi = spring.stop
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2
    if (toeMoment(mid, spring) < moment) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}

/**
 * Die Energie, die bis zu diesem Winkel in der Feder steckt, J.
 *
 * Das Integral der Kennlinie über den Winkel — in BOGENMASS, weil die Rate in
 * Nm je Grad angegeben ist und ein Newtonmeter mal Grad keine Arbeit ist.
 */
export function toeEnergy(deflection: number, spring: ToeSpring = TOE_SPRING): number {
  if (deflection <= 0) return 0
  const steps = 240
  const step = deflection / steps
  let sum = 0
  /* Mittelpunktsregel: bei einer stückweise glatten Kennlinie genau genug und
     ohne Sonderfall am Knick. */
  for (let i = 0; i < steps; i++) sum += toeMoment((i + 0.5) * step, spring) * step
  return (sum * Math.PI) / 180
}

/* -------------------------------------------------------------------------
   Was der Gang von der Zehe verlangt
   ------------------------------------------------------------------------- */

/** Unterhalb dieser Bodenkraft trägt der Fuss nicht. */
const CONTACT_FORCE = 20

/** Ab diesem Moment gilt die Zehe als belastet. */
const LOADED = 1

export interface ToeResult {
  /** Grösstes gefordertes Moment am Gelenk, Nm. */
  demand: number
  /** Winkel, den die Feder dabei einnimmt, Grad. */
  deflection: number
  /** Was bis zum Anschlag noch bleibt, Grad. */
  headroom: number
  /** Gespeicherte Energie bei dieser Auslenkung, J. */
  stored: number
  /** Davon zurückgegeben, J. */
  returned: number
  /** Anteil des Zyklus, in dem die Zehe trägt, 0 … 1. */
  loaded: number
  /** Der Momentenverlauf je Seite, Nm. */
  track: Record<Side, Float32Array>
  /** Steht die Feder am Anschlag? */
  bottomed: boolean
  /** Rechnet die Feder überhaupt mit? */
  on: boolean
}

/**
 * Das geforderte Moment aus Bodenkraft und Druckmittelpunkt.
 *
 * DIE ZEHE TRÄGT ERST, WENN DER DRUCKMITTELPUNKT VOR IHRER ACHSE LIEGT. Solange
 * er dahinter sitzt, drückt der Boden auf den Fuss vor der Achse und das Gelenk
 * bleibt kräftefrei. Das ist der Grund, warum die Zehe je nach Gangart nur
 * vierzehn bis sechsunddreissig Prozent des Zyklus überhaupt Last sieht.
 *
 * Das Moment ist die senkrechte Kraft mal dem Hebel nach vorn, abzüglich der
 * waagerechten Kraft mal dem Hebel nach oben — der zweite Anteil ist klein und
 * gehört trotzdem dazu: beim Abdruck ist die Reibkraft am grössten.
 */
export function toeResult(
  skeleton: Skeleton,
  poses: Pose[],
  grf: Record<Side, Vec3[]>,
  cop: Record<Side, Vec3[]>,
  samples: number,
  on: boolean,
  spring: ToeSpring = TOE_SPRING,
): ToeResult {
  const track = { R: new Float32Array(samples), L: new Float32Array(samples) }
  let demand = 0
  let loaded = 0

  for (const side of SIDES) {
    for (let i = 0; i < samples; i++) {
      const pose = poses[i]
      if (!pose) continue
      const ball = contactPoints(skeleton, pose, side).ball
      const centre = cop[side][i]
      const force = grf[side][i]
      if (!centre || !force) continue

      const ahead = centre.x - ball.x
      const above = centre.y - ball.y
      const moment =
        force.y > CONTACT_FORCE && ahead > 0 ? force.y * ahead - force.x * above : 0

      track[side][i] = moment
      if (moment > demand) demand = moment
      if (moment > LOADED) loaded++
    }
  }

  const deflection = on ? toeDeflection(demand, spring) : 0
  const stored = on ? toeEnergy(deflection, spring) : 0

  return {
    demand,
    deflection,
    headroom: spring.stop - deflection,
    stored,
    returned: stored * spring.recovery,
    loaded: samples > 0 ? loaded / (2 * samples) : 0,
    track,
    /* Am Anschlag heisst: die Bisektion ist an die obere Grenze gelaufen. Dann
       trägt nicht mehr die Feder, sondern die Struktur. */
    bottomed: on && deflection >= spring.stop - 1e-3,
    on,
  }
}
